import { useMemo, useRef, useState } from 'react'

import { F0Box, F0Text } from '@factorialco/f0-react'

import { withCompanySalaryRange } from 'modules/job_catalog/benchmark/lib/companySalaryRange'
import { computeMarketPosition } from 'modules/job_catalog/benchmark/lib/marketPosition'
import SalaryIndicator from 'modules/job_catalog/benchmark/components/SalaryIndicator'
import type {
  BenchmarkAnchorTarget,
  BenchmarkMatchResult,
  MarketPosition,
} from 'modules/job_catalog/benchmark/types'
import type { WorkplaceGeo } from 'modules/job_catalog/benchmark/hooks/useWorkplaceGeo'

import i18n from 'modules/core/lib/i18n'
import type { SalaryByWorkplaceGroup } from 'modules/job_catalog/lib/salaryUtils'

interface SalariesCardProps {
  groupedSalaries: SalaryByWorkplaceGroup[]
  showBenchmarkTrigger: boolean
  onOpenBenchmark: (target: BenchmarkAnchorTarget) => void
  activeBenchmarkWorkplaceId?: string | null
  benchmarkLoading: boolean
  /** Map of workplace ID → { country } for country resolution */
  geoByWorkplaceId?: Map<string, WorkplaceGeo>
  workplaceGeoLoading?: boolean
  /** Fallback country code for "All workplaces" (main legal entity) */
  fallbackCountryCode?: string | null
  /** Shared country → benchmark data map */
  dataByCountry?: Map<string, BenchmarkMatchResult>
  batchBenchmarkLoading?: boolean
}

interface SalariesCardRowProps {
  salaryGroup: SalaryByWorkplaceGroup
  isLast: boolean
  showBenchmarkTrigger: boolean
  onOpenBenchmark: (target: BenchmarkAnchorTarget) => void
  activeBenchmarkWorkplaceId?: string | null
  benchmarkPosition: MarketPosition
  benchmarkLoading: boolean
}

const BACKGROUND_DEFAULT_HOVER = 'var(--f1-color-background-hover, #F3F4F6)'
const SECONDARY_HOVER = 'var(--f1-color-secondary-hover, #0F2E571A)'

const SalariesCardRow = ({
  salaryGroup,
  isLast,
  showBenchmarkTrigger,
  onOpenBenchmark,
  activeBenchmarkWorkplaceId,
  benchmarkPosition,
  benchmarkLoading,
}: SalariesCardRowProps) => {
  const [isSalaryHovered, setIsSalaryHovered] = useState(false)
  const indicatorRef = useRef<HTMLSpanElement>(null)

  const benchmarkWorkplaceId = salaryGroup.groupKey === 'default' ? null : salaryGroup.groupKey
  const isBenchmarkActive = activeBenchmarkWorkplaceId === benchmarkWorkplaceId

  const salaryHoverBackground = isBenchmarkActive
    ? SECONDARY_HOVER
    : isSalaryHovered
      ? BACKGROUND_DEFAULT_HOVER
      : 'transparent'

  const canOpenBenchmark = showBenchmarkTrigger

  return (
    <F0Box
      display="flex"
      flexDirection="row"
      alignItems="center"
      justifyContent="between"
      padding="md"
      borderBottom={isLast ? undefined : 'default'}
      borderColor={isLast ? undefined : 'secondary'}
    >
      <F0Text content={salaryGroup.workplaceLabel} variant="description" />

      {canOpenBenchmark ? (
        /* eslint-disable-next-line factorial/no-raw-html -- Native button required for accessible click target on full salary pill */
        <button
          type="button"
          aria-label={`${i18n.t('job_catalog.benchmark.open_details')}: ${
            salaryGroup.workplaceLabel
          } ${salaryGroup.formattedRange}`}
          style={{
            position: 'relative',
            cursor: 'pointer',
            background: 'transparent',
            transition: 'background-color 120ms ease',
            borderRadius: 200,
            padding: 0,
            border: 'none',
            font: 'inherit',
            overflow: 'visible',
          }}
          onMouseEnter={() => setIsSalaryHovered(true)}
          onMouseLeave={() => setIsSalaryHovered(false)}
          onClick={() => {
            const anchorElement = indicatorRef.current
            if (!anchorElement) return
            onOpenBenchmark({
              workplaceId: benchmarkWorkplaceId,
              anchorElement,
              anchorHorizontalInset: 40,
            })
          }}
        >
          {salaryHoverBackground !== 'transparent' && (
            <span
              aria-hidden
              style={{
                position: 'absolute',
                inset: '0 -4px',
                borderRadius: 200,
                background: salaryHoverBackground,
                pointerEvents: 'none',
              }}
            />
          )}
          {/* eslint-disable-next-line factorial/no-raw-html -- exact z-index and pixel padding not available via F0Box props */}
          <div
            style={{
              position: 'relative',
              zIndex: 1,
              paddingLeft: 4,
              paddingRight: 4,
              display: 'inline-flex',
              alignItems: 'center',
            }}
          >
            {/* eslint-disable-next-line factorial/no-raw-html -- exact gap and pixel padding not available via F0Box props */}
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 2,
                paddingLeft: 2,
                paddingRight: 2,
              }}
            >
              <F0Box>
                <F0Text content={salaryGroup.formattedRange} />
              </F0Box>
              <span ref={indicatorRef} style={{ display: 'inline-flex', alignItems: 'center' }}>
                <SalaryIndicator
                  size="xs"
                  position={benchmarkPosition}
                  loading={benchmarkLoading}
                />
              </span>
            </div>
          </div>
        </button>
      ) : (
        <F0Box display="flex" flexDirection="row" alignItems="center" gap="xs">
          <F0Text content={salaryGroup.formattedRange} />
        </F0Box>
      )}
    </F0Box>
  )
}

const SalariesCard = ({
  groupedSalaries,
  showBenchmarkTrigger,
  onOpenBenchmark,
  activeBenchmarkWorkplaceId,
  benchmarkLoading,
  geoByWorkplaceId,
  workplaceGeoLoading,
  fallbackCountryCode,
  dataByCountry,
  batchBenchmarkLoading,
}: SalariesCardProps) => {
  // Compute market position per workplace group using country-specific benchmark data
  const positionsByGroupKey = useMemo(() => {
    const map = new Map<string, MarketPosition>()
    for (const group of groupedSalaries) {
      // Resolve the country code for this group
      let countryCode: string | null = null
      if (group.groupKey === 'default') {
        countryCode = fallbackCountryCode ?? null
      } else if (geoByWorkplaceId) {
        if (workplaceGeoLoading) {
          countryCode = null
        } else {
          countryCode = geoByWorkplaceId.get(group.groupKey)?.country ?? null
        }
      }

      // Get country-specific benchmark data
      const normalizedCountryCode = countryCode?.toLowerCase() ?? null
      const countryBenchmark = normalizedCountryCode
        ? dataByCountry?.get(normalizedCountryCode)
        : null

      if (countryBenchmark) {
        const position = computeMarketPosition(
          withCompanySalaryRange(countryBenchmark, group.referenceSalary)
        )
        map.set(group.groupKey, position)
      } else {
        map.set(group.groupKey, 'unknown')
      }
    }
    return map
  }, [dataByCountry, fallbackCountryCode, geoByWorkplaceId, groupedSalaries, workplaceGeoLoading])

  return (
    <F0Box border="default" borderColor="secondary" borderRadius="md" overflow="hidden">
      {groupedSalaries.map((salaryGroup, index) => {
        return (
          <SalariesCardRow
            key={salaryGroup.groupKey}
            salaryGroup={salaryGroup}
            isLast={index === groupedSalaries.length - 1}
            showBenchmarkTrigger={showBenchmarkTrigger}
            onOpenBenchmark={onOpenBenchmark}
            activeBenchmarkWorkplaceId={activeBenchmarkWorkplaceId}
            benchmarkPosition={positionsByGroupKey.get(salaryGroup.groupKey) ?? 'unknown'}
            benchmarkLoading={benchmarkLoading || !!batchBenchmarkLoading}
          />
        )
      })}
    </F0Box>
  )
}

export default SalariesCard
